import { ProcessedCourse } from "@/common/course/types";
import CourseEnrollButton from "../CourseEnroll/CourseEnrollButtons";
import AuditLink from "../CourseEnroll/CourseEnrollButtons/AuditLink";
import getCoursePrice from "@/lib/course/utils/getCoursePrice";
import hasAuditSeat from "@/lib/course/utils/hasAuditSeat";
import { isDateInPast } from "@/lib/date";
import useCourseEnrollment from "../CourseEnroll/hooks/useCourseEnrollment";
import { getEnrollButtonText } from "@/lib/course/utils/getCourseRunEnrollButtonText";
import { Skeleton } from "@/components/ui/skeleton";
import useTranslations from "@/i18n/useTranslations";

export interface Props {
  course: ProcessedCourse;
}

export default function MidPageCTAEnrollment({ course }: Props) {
  const t = useTranslations("Course");
  const { activeCourseRun } = course || {};
  const { isEnrollable } = activeCourseRun || {};
  const name = "mid-page-cta";
  const location = "mid-page";

  const enrollmentEnded = activeCourseRun?.enrollmentEnd
    ? isDateInPast(activeCourseRun.enrollmentEnd)
    : false;

  const { isUserEnrolledInRun, isPaidLeadLoading } = useCourseEnrollment({
    courseRun: activeCourseRun!,
    course,
  });

  if (!activeCourseRun) return;
  const courseRunPrice = getCoursePrice(activeCourseRun);

  const enrollButtonMessage = getEnrollButtonText(
    {
      enrollmentStart: activeCourseRun.enrollmentStart,
      enrollmentEnd: activeCourseRun.enrollmentEnd,
      end: activeCourseRun.end,
    },
    isUserEnrolledInRun || false,
  );
  const enrollButtonText = t(
    enrollButtonMessage.key,
    enrollButtonMessage.values,
  );

  if (isPaidLeadLoading) {
    return <Skeleton className="h-11 w-56 rounded-none" />;
  }

  //  Only render the dual-CTA layout when the course actually has
  // an audit seat. Verified-only courses get just the upgrade CTA.
  const showAuditLink = hasAuditSeat(course);

  return (
    <>
      {isEnrollable ? (
        <div className="flex flex-col md:flex-row gap-2 items-center">
          <CourseEnrollButton
            courseRun={activeCourseRun}
            course={course}
            name={name}
            location={location}
            price={courseRunPrice}
          />
          {showAuditLink && (
            <AuditLink
              courseRun={activeCourseRun}
              course={course}
              name={name}
              location={location}
            />
          )}
        </div>
      ) : !isUserEnrolledInRun && enrollmentEnded ? (
        <span>{enrollButtonText}</span>
      ) : null}
    </>
  );
}
